import { extendTailwindMerge } from 'tailwind-merge';

/**
 * `cn()` — join class names so the LAST conflicting utility wins.
 *
 * A component composing `${base} ${variant} ${className}` as a plain string leaves a
 * conflict (`tw-px-4` from the component, `tw-px-2` from the caller) to stylesheet
 * order, which the caller cannot see or control. `cn` resolves it by argument order
 * instead: the caller's class, passed last, replaces the component's.
 *
 * tailwind-merge only knows Tailwind's DEFAULT theme, so every custom key in
 * `tailwind.config.js` is registered here. Without it, `tw-text-title` (a font size)
 * reads as a text COLOUR and deletes `tw-text-ink`, and `tw-bg-ds-cta` (a gradient
 * image) reads as a background colour and deletes the `tw-bg-accent-bold` fallback
 * beneath it. `__tests__/cn.test.ts` fails when the config gains a key this list lacks.
 */
export const TW_CUSTOM = {
  fontSize: [
    'display-2', 'display', 'heading-1', 'heading-2', 'heading-3', 'title-header', 'title',
    'body-large', 'body-primary', 'sub-title-header', 'sub-title-label',
    'sub-body-supporting', 'caption-metadata', 'code-formula',
  ],
  boxShadow: [
    'ds-glow', 'ds-glow-danger', 'r', 'ds-sm', 'ds-sm-up', 'ds-sm-right', 'ds-sm-left',
    'ds-md', 'ds-lg', 'ds-topline', 'ds-tooltip',
  ],
  borderRadius: ['ctl', 'srf', 'ovl'],
  zIndex: ['overlay', 'popover', 'toast', 'tooltip'],
  transitionDuration: ['instant', 'fast', 'base', 'slow'],
  transitionTimingFunction: ['swift', 'spring', 'settle', 'breathe'],
  backgroundImage: ['ds-cta', 'ds-cta-hover'],
  animation: [
    'overlay-fade', 'dialog-pop', 'panel-slide', 'popup-rise', 'mark-pop', 'tick-draw',
    'count-up', 'count-down', 'tip-in', 'overlay-fade-out', 'popup-rise-out',
    'dialog-pop-out', 'panel-slide-out', 'toast-rise', 'toast-rise-out', 'shimmer',
    'toast-timer', 'status-pulse', 'section-reveal', 'fadeIn', 'slideIn',
  ],
} as const;

const twMerge = extendTailwindMerge({
  prefix: 'tw-',
  extend: {
    classGroups: {
      'font-size': [{ text: [...TW_CUSTOM.fontSize] }],
      shadow: [{ shadow: [...TW_CUSTOM.boxShadow] }],
      rounded: [{ rounded: [...TW_CUSTOM.borderRadius] }],
      z: [{ z: [...TW_CUSTOM.zIndex] }],
      duration: [{ duration: [...TW_CUSTOM.transitionDuration] }],
      ease: [{ ease: [...TW_CUSTOM.transitionTimingFunction] }],
      'bg-image': [{ bg: [...TW_CUSTOM.backgroundImage] }],
      animate: [{ animate: [...TW_CUSTOM.animation] }],
    },
  },
});

export type ClassValue = string | false | null | undefined;

export const cn = (...classes: ClassValue[]): string =>
  twMerge(classes.filter(Boolean).join(' '));

/**
 * § 332 — `withClassName(own, className)`: a caller's `className` replaces the component's
 * CONFLICTING classes, and nothing else changes.
 *
 * Deliberately narrower than `cn(own, className)`. `cn` would also re-resolve the
 * component's own classes against EACH OTHER — a variant string that sets `tw-px-3` beside a
 * state that sets `tw-px-0` would suddenly be decided by string order instead of stylesheet
 * order — and that is a visual change nobody asked for. Here the component's string is left
 * exactly as written; only the tokens a caller's class overrides are dropped.
 *
 * With no caller class the result is the component's string, byte for byte.
 */
export const withClassName = (own: string, className?: ClassValue): string => {
  const caller = typeof className === 'string' ? className.trim() : '';
  if (!caller) return own;
  const kept = own
    .split(/\s+/)
    .filter((token) => token && twMerge(`${token} ${caller}`).split(' ').includes(token));
  return `${kept.join(' ')} ${caller}`;
};
